/**
 * Carousel vidéo vertical SharpMod — texte à gauche, pistes scroll-snap, flou sur les côtés.
 */
class SmVideoShowcase extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.sm-vs__track');
    if (!this.track) return;

    this.slides = () => Array.from(this.track.querySelectorAll('.sm-vs__slide'));
    this.btn = this.querySelector('.sm-vs__cta');
    this.btnText = this.querySelector('.sm-vs__cta-text');
    this.btnPrev = this.querySelector('.sm-vs__nav--prev');
    this.btnNext = this.querySelector('.sm-vs__nav--next');
    this.openNewTab = this.dataset.openNewTab === 'true';

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.currentActiveSlide = null;
    this._scrollTicking = false;

    this.carouselBox = this.querySelector('.sm-vs__carousel-box');
    this.carouselBox?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    if (this.slides().length === 0) return;

    this.track.addEventListener(
      'scroll',
      () => {
        if (!this._scrollTicking) {
          window.requestAnimationFrame(() => {
            this.renderCarousel();
            this._scrollTicking = false;
          });
          this._scrollTicking = true;
        }
      },
      { passive: true }
    );

    this.slides().forEach((slide) => {
      slide.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollSlideToCenter(slide);
      });
    });

    this.btnPrev?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.stepSlide(-1);
    });
    this.btnNext?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.stepSlide(1);
    });

    this.btnPrev?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      this.stepSlide(-1);
    });
    this.btnNext?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      this.stepSlide(1);
    });

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => this.renderCarousel());
    });

    this.renderCarousel();
    setTimeout(() => this.renderCarousel(), 150);
  }

  scrollSlideToCenter(slide) {
    const track = this.track;
    const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
    const scrollTarget = slideCenter - track.clientWidth / 2;
    track.scrollTo({
      left: scrollTarget,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  stepSlide(delta) {
    const slides = this.slides();
    const s = this.currentActiveSlide;
    if (!s || slides.length === 0) return;
    const idx = slides.indexOf(s);
    if (idx < 0) return;
    const target = delta < 0 ? slides[idx - 1] : slides[idx + 1];
    if (target) this.scrollSlideToCenter(target);
  }

  renderCarousel() {
    const slides = this.slides();
    if (slides.length === 0) return;

    const track = this.track;
    const trackCenter = track.scrollLeft + track.clientWidth / 2;

    let closestSlide = null;
    let minDistance = Infinity;
    const gap = parseFloat(getComputedStyle(track).gap) || 24;

    slides.forEach((slide) => {
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(trackCenter - slideCenter);
      const maxDistance = slide.clientWidth + gap;
      let ratio = distance / maxDistance;
      ratio = Math.max(0, Math.min(1, ratio));

      const scale = 1 - ratio * 0.12;
      const blur = ratio * 6;
      const opacity = 1 - ratio * 0.35;

      slide.style.transform = `scale(${scale})`;
      slide.style.filter = `blur(${blur}px)`;
      slide.style.opacity = String(opacity);

      if (distance < minDistance) {
        minDistance = distance;
        closestSlide = slide;
      }
    });

    if (!closestSlide) return;
    this.currentActiveSlide = closestSlide;

    const single = slides.length <= 1;
    const idx = slides.indexOf(closestSlide);
    if (this.btnPrev) {
      this.btnPrev.classList.toggle('is-hidden', single || idx <= 0);
    }
    if (this.btnNext) {
      this.btnNext.classList.toggle('is-hidden', single || idx >= slides.length - 1);
    }

    const link = closestSlide.dataset.link || '#';
    const title = closestSlide.dataset.title || '';

    if (this.btn) {
      this.btn.setAttribute('href', link);
      if (this.openNewTab) {
        this.btn.setAttribute('target', '_blank');
        this.btn.setAttribute('rel', 'noopener noreferrer');
      } else {
        this.btn.removeAttribute('target');
        this.btn.removeAttribute('rel');
      }
      if (link === '#' || !link) {
        this.btn.setAttribute('aria-disabled', 'true');
        this.btn.classList.add('sm-vs__cta--muted');
      } else {
        this.btn.removeAttribute('aria-disabled');
        this.btn.classList.remove('sm-vs__cta--muted');
      }
    }
    if (this.btnText) {
      this.btnText.textContent = title;
    }

    slides.forEach((s) => {
      const video = s.querySelector('video');
      if (!video) return;
      if (s === closestSlide) {
        if (video.paused) {
          video.play().catch(() => {});
        }
      } else if (!video.paused) {
        video.pause();
      }
    });
  }
}

if (!customElements.get('sm-video-showcase')) {
  customElements.define('sm-video-showcase', SmVideoShowcase);
}
